import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';

import {FormsModule} from '@angular/forms';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {TableModule} from 'primeng/table';
import {DatePicker} from 'primeng/datepicker';
import {Select} from 'primeng/select';
import {Button} from 'primeng/button';
import {
  ParticipatingToolControllerService,
  RfdAppointmentStatus,
  RfdPlace,
  RfdScheduleUpdateRequest,
  TenderContactPersonResponse,
  TenderPhaseType,
  TenderStatus,
} from '@frontend/shared-api';
import {InvitationDataLoaderService} from '../../../../../../../../shared/services/invitation-data-loader.service';
import {HrTool} from '@frontend/directus';
import {ToastService} from '@frontend/toast';
import {ConfirmStepLeaveDirective} from '../../../../../../../../shared/project-step-save-button/confirm-step-leave.directive';
import {ProviderContactModalComponent} from '../provider-contact-modal/provider-contact-modal.component';
import {NoDataComponent} from '../../../../../../../../shared/components/no-data/no-data.component';
import {firstValueFrom} from 'rxjs';
import {LoadingComponent} from '../../../../../../../../shared/components/loading/loading.component';
import {RfdScheduleCalendarComponent} from '../rfd-schedule-calendar/rfd-schedule-calendar.component';
import {RfdCalendarAppointment} from '../rfd-schedule-calendar/rfd-schedule-calendar.types';

interface RfdScheduleRow {
  participatingToolId: number;
  hrToolDirectusId: string;
  tenderStatus: TenderStatus;
  providerName: string;
  hrToolDisplayName: string | null;
  rfdDatetime: Date | null;
  rfdPlace: RfdPlace | null;
  rfdAppointmentStatus: RfdAppointmentStatus | null;
}

@Component({
  selector: 'app-rfd-schedule-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslocoPipe,
    TableModule,
    DatePicker,
    Select,
    Button,
    ConfirmStepLeaveDirective,
    ProviderContactModalComponent,
    NoDataComponent,
    LoadingComponent,
    RfdScheduleCalendarComponent,
  ],
  templateUrl: './rfd-schedule-table.component.html',
})
export class RfdScheduleTableComponent implements OnInit {
  private readonly transloco = inject(TranslocoService);
  private readonly invitationDataLoader = inject(InvitationDataLoaderService);
  private readonly participatingToolService = inject(ParticipatingToolControllerService);
  private readonly toast = inject(ToastService);

  readonly projectId = input.required<number>();
  readonly stepLocked = input<boolean>(false);

  protected readonly loading = signal(false);
  protected readonly rows = signal<RfdScheduleRow[]>([]);
  private readonly originalValues = signal<
    Map<
      number,
      {
        datetime: string | null;
        place: RfdPlace | null;
        appointmentStatus: RfdAppointmentStatus | null;
      }
    >
  >(new Map());
  private readonly pendingDatetimeById = signal<Map<number, Date | null>>(new Map());
  private readonly visibleStatuses = new Set<TenderStatus>([
    TenderStatus.ACCEPTED,
    TenderStatus.FINISHED,
  ]);

  protected readonly contactModalVisible = signal(false);
  protected readonly contactModalLoading = signal(false);
  protected readonly contactModalProviderName = signal<string | null>(null);
  protected readonly contactModalContact = signal<TenderContactPersonResponse | null>(null);

  protected readonly placeOptions = computed(() => [
    {
      label: this.transloco.translate('project.steps.rfd.invitationList.place.remote'),
      value: RfdPlace.REMOTE,
    },
    {
      label: this.transloco.translate('project.steps.rfd.invitationList.place.onSite'),
      value: RfdPlace.ON_SITE,
    },
  ]);

  protected readonly appointmentStatusOptions = computed(() => [
    {
      label: this.transloco.translate('project.steps.rfd.invitationList.appointmentStatus.planned'),
      value: RfdAppointmentStatus.PLANNED,
    },
    {
      label: this.transloco.translate(
        'project.steps.rfd.invitationList.appointmentStatus.confirmed',
      ),
      value: RfdAppointmentStatus.CONFIRMED,
    },
  ]);

  protected readonly calendarAppointments = computed((): RfdCalendarAppointment[] =>
    this.rows()
      .filter((row): row is RfdScheduleRow & {rfdDatetime: Date} => row.rfdDatetime !== null)
      .map((row) => ({
        participatingToolId: row.participatingToolId,
        start: row.rfdDatetime,
        providerName: row.providerName,
        toolName: row.hrToolDisplayName,
        place: row.rfdPlace,
        appointmentStatus: row.rfdAppointmentStatus,
      })),
  );

  protected readonly hasChanges = computed(() => {
    const originals = this.originalValues();
    return this.rows().some((row) => {
      const original = originals.get(row.participatingToolId);
      if (!original) return true;
      const currentDate = row.rfdDatetime ? row.rfdDatetime.toISOString() : null;
      return (
        currentDate !== original.datetime ||
        row.rfdPlace !== original.place ||
        row.rfdAppointmentStatus !== original.appointmentStatus
      );
    });
  });

  ngOnInit(): void {
    void this.loadData();
  }

  protected async loadData(): Promise<void> {
    this.loading.set(true);
    try {
      const {directusHrTools, participatingToolsResponse} = await firstValueFrom(
        this.invitationDataLoader.loadParticipatingToolsData(this.projectId(), TenderPhaseType.RFD),
      );

      const scheduleRows = participatingToolsResponse.tools
        .filter((tool) => tool.rfdStatus !== undefined && this.visibleStatuses.has(tool.rfdStatus))
        .map((rfdTool) => {
          if (rfdTool.rfdStatus === undefined) return null;
          const hrTool = directusHrTools.find((tool) => tool.id === rfdTool.hrToolDirectusId);
          if (!hrTool) return null;
          return this.mapToScheduleRow(
            hrTool,
            rfdTool.rfdStatus,
            rfdTool.rfdDatetime ?? null,
            rfdTool.rfdPlace ?? null,
            rfdTool.rfdAppointmentStatus ?? null,
            rfdTool.id,
          );
        })
        .filter((row): row is RfdScheduleRow => row !== null)
        .sort((a, b) =>
          a.providerName.localeCompare(b.providerName, undefined, {
            sensitivity: 'base',
          }),
        );

      const originalMap = new Map<
        number,
        {
          datetime: string | null;
          place: RfdPlace | null;
          appointmentStatus: RfdAppointmentStatus | null;
        }
      >();
      scheduleRows.forEach((row) => {
        originalMap.set(row.participatingToolId, {
          datetime: row.rfdDatetime ? row.rfdDatetime.toISOString() : null,
          place: row.rfdPlace ?? null,
          appointmentStatus: row.rfdAppointmentStatus ?? null,
        });
      });

      this.rows.set(scheduleRows);
      this.originalValues.set(originalMap);
      this.pendingDatetimeById.set(new Map());
    } finally {
      this.loading.set(false);
    }
  }

  private mapToScheduleRow(
    tool: HrTool,
    tenderStatus: TenderStatus,
    rfdDatetime: string | null,
    rfdPlace: RfdPlace | null,
    rfdAppointmentStatus: RfdAppointmentStatus | null,
    participatingToolId: number,
  ): RfdScheduleRow {
    const lang = this.transloco.getActiveLang();
    const translation = tool.translations.find((t) => t.language === lang);
    return {
      participatingToolId,
      hrToolDirectusId: tool.id,
      tenderStatus,
      providerName: tool.toolProvider?.name ?? '',
      hrToolDisplayName: (translation?.name?.trim() !== '' ? translation?.name : tool.name) ?? null,
      rfdDatetime: rfdDatetime ? new Date(rfdDatetime) : null,
      rfdPlace,
      rfdAppointmentStatus,
    };
  }

  protected getPendingDatetime(row: RfdScheduleRow): Date | null {
    return this.pendingDatetimeById().get(row.participatingToolId) ?? row.rfdDatetime;
  }

  protected onDatetimeChange(row: RfdScheduleRow, value: Date | null): void {
    const map = new Map(this.pendingDatetimeById());
    map.set(row.participatingToolId, value);
    this.pendingDatetimeById.set(map);
  }

  protected applyDatetimeOnSelectButton(row: RfdScheduleRow): void {
    this.applyPendingDatetime(row);
  }

  protected applyDatetimeOnPickerClose(row: RfdScheduleRow): void {
    this.applyPendingDatetime(row);
  }

  private applyPendingDatetime(row: RfdScheduleRow): void {
    const nextValue = this.getApplicablePendingDatetime(row);
    if (nextValue === undefined) {
      return;
    }
    this.updateRow(row.participatingToolId, {rfdDatetime: nextValue});
  }

  protected isApplyDisabled(row: RfdScheduleRow): boolean {
    if (this.isRowInputDisabled(row)) {
      return true;
    }
    return this.getApplicablePendingDatetime(row) === undefined;
  }

  protected isRowInputDisabled(row: RfdScheduleRow): boolean {
    return this.stepLocked() || row.tenderStatus === TenderStatus.FINISHED;
  }

  private getApplicablePendingDatetime(row: RfdScheduleRow): Date | null | undefined {
    const pendingMap = this.pendingDatetimeById();
    if (!pendingMap.has(row.participatingToolId)) {
      return undefined;
    }

    const pending = this.pendingDatetimeById().get(row.participatingToolId) ?? null;
    const current = row.rfdDatetime ?? null;

    if (pending === null && current !== null) {
      return undefined;
    }

    const pendingValue = pending ? pending.toISOString() : null;
    const currentValue = current ? current.toISOString() : null;
    return pendingValue === currentValue ? undefined : pending;
  }

  protected onPlaceChange(row: RfdScheduleRow, value: RfdPlace | null): void {
    this.updateRow(row.participatingToolId, {rfdPlace: value});
  }

  protected onAppointmentStatusChange(
    row: RfdScheduleRow,
    value: RfdAppointmentStatus | null,
  ): void {
    this.updateRow(row.participatingToolId, {rfdAppointmentStatus: value});
  }

  private updateRow(id: number, updates: Partial<RfdScheduleRow>): void {
    const updatedRows = this.rows().map((row) =>
      row.participatingToolId === id ? {...row, ...updates} : row,
    );
    this.rows.set(updatedRows);
  }

  protected async onSave(): Promise<void> {
    if (!this.hasChanges() || this.stepLocked()) {
      return;
    }

    const originals = this.originalValues();
    const updatedRows = this.rows().filter((row) => {
      const original = originals.get(row.participatingToolId);
      if (!original) return true;
      const currentDate = row.rfdDatetime ? row.rfdDatetime.toISOString() : null;
      return (
        currentDate !== original.datetime ||
        row.rfdPlace !== original.place ||
        row.rfdAppointmentStatus !== original.appointmentStatus
      );
    });

    const requests = updatedRows.map((row) => {
      const request: RfdScheduleUpdateRequest = {
        rfdDatetime: row.rfdDatetime ? row.rfdDatetime.toISOString() : undefined,
        rfdPlace: row.rfdPlace ?? undefined,
        rfdAppointmentStatus: row.rfdAppointmentStatus ?? undefined,
      };
      return firstValueFrom(
        this.participatingToolService.updateRfdSchedule({
          projectId: this.projectId(),
          phase: TenderPhaseType.RFD,
          participatingToolId: row.participatingToolId,
          rfdScheduleUpdateRequest: request,
        }),
      );
    });

    await Promise.all(requests);
    await this.loadData();
    await this.toast.showSuccess('common.success', 'project.steps.sectionSaved');
  }

  protected async openContact(row: RfdScheduleRow): Promise<void> {
    const providerName = row.providerName?.trim();
    this.contactModalProviderName.set(
      providerName && providerName.length > 0 ? providerName : null,
    );
    this.contactModalContact.set(null);
    this.contactModalVisible.set(true);
    this.contactModalLoading.set(true);

    try {
      const contact = await firstValueFrom(
        this.participatingToolService.getHrToolContactPerson({
          projectId: this.projectId(),
          phase: TenderPhaseType.RFD,
          hrToolDirectusId: row.hrToolDirectusId,
        }),
      );
      this.contactModalContact.set(contact as TenderContactPersonResponse | null);
    } finally {
      this.contactModalLoading.set(false);
    }
  }
}
